import React, { useState } from "react";
import { authHeaders } from '../lib/apiAuth';
import { T, SPARKY_SERVER_URL, SUBJECTS } from './shared/constants';
import { Badge } from './shared/ui';

export default function StudyPlanner({ examProgram, yearId, onClose, onStartPractice }) {
  const [examDate, setExamDate] = useState(
    localStorage.getItem(`lumio_exam_date_${examProgram}`) || ""
  );
  const [plan, setPlan]         = useState(() => {
    const saved = localStorage.getItem(`lumio_study_plan_${examProgram}`);
    return saved ? JSON.parse(saved) : null;
  });
  const [generating, setGenerating] = useState(false);
  const [phase, setPhase]           = useState(plan ? "plan" : "setup");

  const diagnostic = JSON.parse(localStorage.getItem("lumio_diagnostic") || "null");

  const SUBJECT_ROTATION = {
    naplan:        ["reading","writing","numeracy","language"],
    selective_nsw: ["reading","numeracy","thinking","writing"],
    oc:            ["reading","numeracy","thinking"],
    acer:          ["reading","numeracy","thinking"],
    icas:          ["reading","numeracy","science","digital"],
    vic_selective: ["reading","numeracy","thinking","writing"],
  };

  const subjects = SUBJECT_ROTATION[examProgram] || ["reading","numeracy","thinking","language"];

  const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  async function generatePlan() {
    setGenerating(true);

    const daysUntil = examDate ? Math.ceil((new Date(examDate) - new Date()) / 86400000) : 56;
    const weeks = Math.min(8, Math.max(1, Math.floor(daysUntil / 7)));
    const diagContext = diagnostic
      ? `Diagnostic scores: ${Object.entries(diagnostic.scores || {}).map(([k,v]) => `${k}: ${v.pct}%`).join(", ")}`
      : "No diagnostic completed yet";

    try {
      const res = await fetch(SPARKY_SERVER_URL + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        body: JSON.stringify({
          model: "claude-haiku-5-5",
          max_tokens: 600,
          system: `You are Lumi, creating a study plan. Respond ONLY with valid JSON \u2014 no other text. Format: {"weeks":[{"week":1,"theme":"...","days":[{"day":"Mon","subject":"reading","task":"...","mins":15},...]},...]}.
Subjects available: ${subjects.join(", ")}. Keep task descriptions under 8 words. Vary subjects across the week. Focus more on weaker subjects.`,
          messages: [{
            role: "user",
            content: `Create a ${weeks}-week study plan for ${examProgram} (${yearId}). ${diagContext}. Exam in ${daysUntil} days. Include all 7 days per week. Each day 10-20 minutes.`
          }],
        }),
      });
      const data = await res.json();
      const text = data?.content?.[0]?.text || "";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setPlan(parsed);
      localStorage.setItem(`lumio_study_plan_${examProgram}`, JSON.stringify(parsed));
      setPhase("plan");
    } catch(e) {
      const fallbackPlan = { weeks: Array.from({ length: Math.min(4, Math.floor(daysUntil/7)) || 1 }, (_, wi) => ({
        week: wi + 1,
        theme: wi === 0 ? "Foundation" : wi === 1 ? "Building Skills" : wi === 2 ? "Practice" : "Exam Ready",
        days: DAY_LABELS.map((day, di) => {
          const weekSubjects = [...subjects.slice(wi % subjects.length), ...subjects.slice(0, wi % subjects.length)];
          const subj = weekSubjects[di % weekSubjects.length];
          const weekTasks = {
            0: ["Review key concepts","Practice questions","Focus on weak areas","Timed exercises","Mixed practice","Light review","Rest day"],
            1: ["Deep practice","Exam-style questions","Topic drill","Worked examples","Mock test","Error analysis","Rest day"],
            2: ["Speed practice","Hard questions only","Full topic sweep","Timed test","Revision","Past papers","Rest day"],
            3: ["Mock exam conditions","Full past paper","Weak spots focus","Final revision","Light practice","Exam prep","Rest day"],
          };
          const taskList = weekTasks[wi] || weekTasks[0];
          const taskName = taskList[di % taskList.length];
          return {
            day, subject: subj,
            // Don't append a subject to "Rest day" — it's contradictory
            task: taskName === 'Rest day' ? 'Rest day' : `${taskName} \u2014 ${subj}`,
            mins: taskName === 'Rest day' ? null : 15 + (wi * 5),
          };
        }),
      })) };
      setPlan(fallbackPlan);
      localStorage.setItem(`lumio_study_plan_${examProgram}`, JSON.stringify(fallbackPlan));
      setPhase("plan");
    }
    setGenerating(false);
  }

  const today = new Date();
  const todayDay = DAY_LABELS[today.getDay() === 0 ? 6 : today.getDay() - 1];

  if (phase === "setup") return (
    <div style={{ padding:"32px 24px", maxWidth:500, margin:"0 auto", textAlign:"center" }}>
      <div style={{ fontSize:52, marginBottom:16 }}>{"\uD83D\uDCC5"}</div>
      <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:28, color:T.text, marginBottom:8 }}>Study Planner</h2>
      <p style={{ fontSize:14, color:T.textMid, lineHeight:1.7, marginBottom:24 }}>
        Lumi will create a personalised daily study plan based on your exam date
        {diagnostic ? " and your diagnostic results" : ""}.
      </p>
      <label style={{ fontSize:13, fontWeight:700, color:T.text, display:"block", textAlign:"left", marginBottom:8 }}>Exam date</label>
      <input type="date" value={examDate} onChange={e => { setExamDate(e.target.value); localStorage.setItem(`lumio_exam_date_${examProgram}`, e.target.value); }}
        style={{ width:"100%", padding:"13px 16px", background:T.surface, border:`1.5px solid ${T.border}`, borderRadius:12, color:T.text, fontSize:15, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:"none", boxSizing:"border-box", marginBottom:20 }} />
      <button onClick={generatePlan} disabled={!examDate} style={{ width:"100%", padding:14, background:examDate?T.accent:T.border, border:"none", borderRadius:12, color:"white", fontWeight:800, fontSize:16, cursor:examDate?"pointer":"not-allowed", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        Generate My Study Plan {"\u2192"}
      </button>
    </div>
  );

  if (generating) return (
    <div style={{ padding:48, textAlign:"center" }}>
      <div style={{ fontSize:48, marginBottom:16, animation:"spin 2s linear infinite", display:"inline-block" }}>{"\u2728"}</div>
      <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:24, color:T.text, marginBottom:8 }}>Lumi is building your plan...</h2>
      <p style={{ fontSize:14, color:T.textMid }}>Personalising based on your exam and progress</p>
    </div>
  );

  if (phase === "plan" && plan) return (
    <div style={{ padding:"0 0 80px" }}>
      <div style={{ padding:"16px 20px", background:`${T.accent}10`, borderBottom:`1px solid ${T.accent}22` }}>
        <p style={{ fontSize:12, color:T.accent, fontWeight:700, margin:"0 0 4px", textTransform:"uppercase", letterSpacing:".08em" }}>Your personalised study plan</p>
        <p style={{ fontSize:13, color:T.textMid, margin:0 }}>{plan.weeks?.length} weeks {"\u00B7"} 10-20 min/day {"\u00B7"} Tailored to your gaps</p>
      </div>
      {plan.weeks?.map((week, wi) => (
        <div key={wi} style={{ marginBottom:4 }}>
          <div style={{ padding:"12px 20px 8px", background:T.surface }}>
            <p style={{ fontSize:12, fontWeight:800, color:T.textMid, margin:0, textTransform:"uppercase", letterSpacing:".08em" }}>Week {week.week} {"\u2014"} {week.theme}</p>
          </div>
          <div style={{ padding:"0 12px" }}>
            {week.days?.map((day, di) => {
              const subj = SUBJECTS.find(s => s.id === day.subject);
              const isToday = day.day === todayDay && wi === 0;
              return (
                <div key={di} onClick={() => { if(day.subject && onStartPractice) { onStartPractice(day.subject, yearId); } }} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 12px", margin:"6px 0", background:isToday?`${T.accent}15`:T.card, border:`1px solid ${isToday?T.accent:T.border}`, borderRadius:12, transition:"all .2s", cursor:day.subject?"pointer":"default" }} onMouseOver={e=>{if(day.subject)e.currentTarget.style.borderColor=T.accent}} onMouseOut={e=>e.currentTarget.style.borderColor=isToday?T.accent:T.border}>
                  <div style={{ width:36, height:36, borderRadius:8, background:subj ? subj.lo : T.surface, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                    {subj?.icon || "\uD83D\uDCDA"}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                      <span style={{ fontSize:12, fontWeight:800, color:isToday?T.accent:T.textMid }}>{day.day}</span>
                      {isToday && <Badge label="Today" color={T.accent} />}
                    </div>
                    <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{day.task}</p>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <p style={{ fontSize:12, color:T.textMid, margin:0 }}>{day.mins != null ? `${day.mins}min` : "Rest"}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ padding:"16px 20px" }}>
        <button onClick={() => { setPlan(null); setPhase("setup"); }} style={{ width:"100%", padding:12, background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, color:T.textMid, fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
          Regenerate Plan
        </button>
      </div>
    </div>
  );

  return null;
}
